/**
 * Real-time 3D try-on for Floor202.
 *
 * The frame is built from the product photographs: the front outline and both
 * temples are extruded to their real thickness, and each colour's photograph
 * is mapped onto them. PD sets the millimetre scale. Nothing is sent to a server.
 */
const VISION_PKG = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.1.1'
const WASM_BASE = `${VISION_PKG}/wasm`
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.186.1/build/three.module.js'
const FACE_MODEL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task'
const HAIR_MODEL = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite'
const HAIR_CATEGORY = 1
const STORAGE_KEY = 'e8-vto-pd'
const DEFAULT_PD = 63
const PD_MIN = 50
const PD_MAX = 80
const FRONT_DEPTH_MM = 4.2
const TEMPLE_DEPTH_MM = 1.6
const TEMPLE_SPLAY = 0.02
const VERTEX_DISTANCE_MM = 12
const LENS_BASE_RADIUS_MM = 140
const VIEW_DISTANCE_MM = 450
const FAR_TEMPLE_HIDE_MM = 10
const FAR_TEMPLE_SHOW_MM = 5
const STAGE_WIDTH = 576
const DESKTOP_ASPECT = 4 / 5
const FULL_SCREEN_QUERY = '(max-width: 749px)'

const roots = new Map()
const stageSize = { height: STAGE_WIDTH / DESKTOP_ASPECT, faceY: 0.46 }
let threeModule = null
const shapeData = new Map()

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

/**
 * @param {string} raw
 * @returns {{pd: number, custom: boolean, invalid: boolean}}
 */
function parsePd(raw) {
  const text = String(raw || '').trim()
  if (!text) return { pd: DEFAULT_PD, custom: false, invalid: false }
  const value = Number(text)
  if (!Number.isFinite(value) || value < PD_MIN || value > PD_MAX) {
    return { pd: DEFAULT_PD, custom: false, invalid: true }
  }
  return { pd: value, custom: true, invalid: false }
}

/**
 * Frame dimensions from the try-on frame metaobject; Floor202's when missing.
 * @param {DOMStringMap} dataset
 * @returns {{frameWidth: number, dbc: number, bridge: number}}
 */
function readSpec(dataset) {
  const number = (value, fallback) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
  }
  const lensWidth = number(dataset.vtoLensWidth, 48)
  const bridge = number(dataset.vtoBridge, 21)
  return { frameWidth: number(dataset.vtoFrameWidth, 145), dbc: lensWidth + bridge, bridge }
}

/**
 * PD against the lens-centre distance decides; face width only breaks a tie.
 * @param {number} pd
 * @param {number|null} faceWidth
 * @param {{frameWidth: number, dbc: number}} spec
 * @returns {'close'|'large'|'small'}
 */
function judgeFit(pd, faceWidth, spec) {
  const pdGap = spec.dbc - pd
  let pdVote = 0
  if (pdGap > 4) pdVote = 1
  else if (pdGap < -4) pdVote = -1
  let faceVote = 0
  if (faceWidth) {
    if (spec.frameWidth > faceWidth + 12) faceVote = 1
    else if (spec.frameWidth < faceWidth - 4) faceVote = -1
  }
  const vote = pdVote || faceVote
  if (vote > 0) return 'large'
  if (vote < 0) return 'small'
  return 'close'
}

/**
 * @param {string} template
 * @param {Record<string, string|number>} values
 * @returns {string}
 */
function fill(template, values) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ''))
}

/**
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`image ${url}`))
    image.src = url
  })
}

/**
 * @returns {Promise<object>}
 */
async function loadThree() {
  if (!threeModule) threeModule = await import(THREE_URL)
  return threeModule
}

/**
 * @param {string} url
 * @returns {Promise<object>}
 */
async function loadShapes(url) {
  if (!shapeData.has(url)) {
    shapeData.set(url, fetch(url).then((response) => {
      if (!response.ok) throw new Error(`shape ${response.status}`)
      return response.json()
    }))
  }
  return shapeData.get(url)
}

/**
 * One Euro filter: steady when the face is still, quick when it moves.
 */
class OneEuro {
  /**
   * @param {number} minCutoff Hz at rest; lower is steadier.
   * @param {number} beta How fast the cutoff rises with speed.
   */
  constructor(minCutoff, beta) {
    this.minCutoff = minCutoff
    this.beta = beta
    this.value = null
    this.speed = 0
    this.time = 0
  }

  static alpha(cutoff, dt) {
    const tau = 1 / (2 * Math.PI * cutoff)
    return 1 / (1 + tau / dt)
  }

  reset() {
    this.value = null
  }

  /**
   * @param {number} value
   * @param {number} time Seconds.
   * @returns {number}
   */
  filter(value, time) {
    if (this.value === null) {
      this.value = value
      this.speed = 0
      this.time = time
      return value
    }
    const dt = Math.max(time - this.time, 1 / 120)
    this.time = time
    const speed = (value - this.value) / dt
    this.speed += (speed - this.speed) * OneEuro.alpha(1, dt)
    const cutoff = this.minCutoff + this.beta * Math.abs(this.speed)
    this.value += (value - this.value) * OneEuro.alpha(cutoff, dt)
    return this.value
  }
}

/**
 * One Euro filter for a rotation, using the angular speed.
 */
class OneEuroRotation {
  /**
   * @param {object} THREE
   * @param {number} minCutoff
   * @param {number} beta
   */
  constructor(THREE, minCutoff, beta) {
    this.value = new THREE.Quaternion()
    this.ready = false
    this.minCutoff = minCutoff
    this.beta = beta
    this.speed = 0
    this.time = 0
  }

  reset() {
    this.ready = false
  }

  /**
   * @param {object} rotation
   * @param {number} time Seconds.
   * @returns {object}
   */
  filter(rotation, time) {
    if (!this.ready) {
      this.value.copy(rotation)
      this.ready = true
      this.speed = 0
      this.time = time
      return this.value
    }
    const dt = Math.max(time - this.time, 1 / 120)
    this.time = time
    const speed = this.value.angleTo(rotation) / dt
    this.speed += (speed - this.speed) * OneEuro.alpha(1, dt)
    const cutoff = this.minCutoff + this.beta * this.speed
    this.value.slerp(rotation, OneEuro.alpha(cutoff, dt))
    return this.value
  }
}

const modelData = new Map()
const SOLID_ALPHA = 220
const ACETATE_OPACITY = { brown: 0.9, clear: 0.62 }
const BLEED_INSET_PX = 3

/**
 * Reads the GLB written by scripts/build-e8-vto-model.py: one primitive per
 * named mesh, float positions, normalised int8 normals, uint32 indices.
 * @param {object} THREE
 * @param {ArrayBuffer} buffer
 * @returns {Record<string, object>} BufferGeometry by mesh name.
 */
function parseGlb(THREE, buffer) {
  const view = new DataView(buffer)
  if (view.getUint32(0, true) !== 0x46546c67) throw new Error('model is not a GLB')
  const jsonLength = view.getUint32(12, true)
  const gltf = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 20, jsonLength)))
  const binary = 20 + jsonLength + 8
  const types = { 5126: Float32Array, 5125: Uint32Array, 5123: Uint16Array, 5120: Int8Array }
  const sizes = { SCALAR: 1, VEC3: 3 }
  const read = (index) => {
    const accessor = gltf.accessors[index]
    const bufferView = gltf.bufferViews[accessor.bufferView]
    const Type = types[accessor.componentType]
    const size = sizes[accessor.type]
    const stride = bufferView.byteStride ? bufferView.byteStride / Type.BYTES_PER_ELEMENT : size
    const offset = binary + (bufferView.byteOffset || 0) + (accessor.byteOffset || 0)
    const raw = new Type(buffer, offset, accessor.count * stride)
    if (stride === size) return { array: raw, size, normalized: !!accessor.normalized }
    const packed = new Type(accessor.count * size)
    for (let item = 0; item < accessor.count; item += 1) {
      for (let part = 0; part < size; part += 1) packed[item * size + part] = raw[item * stride + part]
    }
    return { array: packed, size, normalized: !!accessor.normalized }
  }
  const geometries = {}
  gltf.meshes.forEach((mesh) => {
    const primitive = mesh.primitives[0]
    const geometry = new THREE.BufferGeometry()
    const position = read(primitive.attributes.POSITION)
    const normal = read(primitive.attributes.NORMAL)
    geometry.setAttribute('position', new THREE.BufferAttribute(position.array, 3))
    geometry.setAttribute('normal', new THREE.BufferAttribute(normal.array, 3, normal.normalized))
    geometry.setIndex(new THREE.BufferAttribute(read(primitive.indices).array, 1))
    geometry.computeBoundingBox()
    geometries[mesh.name] = geometry
  })
  return geometries
}

/**
 * @param {object} THREE
 * @param {string} url
 * @returns {Promise<Record<string, object>>}
 */
async function loadModel(THREE, url) {
  if (!modelData.has(url)) {
    modelData.set(url, fetch(url).then((response) => {
      if (!response.ok) throw new Error(`model ${response.status}`)
      return response.arrayBuffer()
    }).then((buffer) => parseGlb(THREE, buffer)))
  }
  return modelData.get(url)
}

/**
 * Product cut-outs have transparent surroundings. Planar projection onto the
 * model samples slightly past the photographed edge, so the edge colour is
 * pushed outward and the rest is filled with the rim colour.
 * @param {object} THREE
 * @param {HTMLImageElement} image
 * @param {string} fillColor
 * @param {number[][][] & {map: object}} [holes] Lens openings in mm, cut out
 *   so the front is trimmed exactly along the photographed rim.
 * @returns {object}
 */
function bleedTexture(THREE, image, fillColor, holes) {
  const width = image.naturalWidth || image.width
  const height = image.naturalHeight || image.height
  const solid = document.createElement('canvas')
  solid.width = width
  solid.height = height
  const solidContext = solid.getContext('2d', { willReadFrequently: true })
  solidContext.drawImage(image, 0, 0)
  const pixels = solidContext.getImageData(0, 0, width, height)
  for (let offset = 3; offset < pixels.data.length; offset += 4) {
    pixels.data[offset] = pixels.data[offset] > SOLID_ALPHA ? 255 : 0
  }
  solidContext.putImageData(pixels, 0, 0)
  // Rim edges carry the studio highlight, so the bleed samples a little inside them.
  const core = document.createElement('canvas')
  core.width = width
  core.height = height
  const coreContext = core.getContext('2d')
  coreContext.drawImage(solid, 0, 0)
  coreContext.globalCompositeOperation = 'destination-in'
  for (let step = 0; step < 8; step += 1) {
    const angle = step * Math.PI / 4
    coreContext.drawImage(solid, Math.round(Math.cos(angle) * BLEED_INSET_PX), Math.round(Math.sin(angle) * BLEED_INSET_PX))
  }
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  context.drawImage(solid, 0, 0)
  context.globalCompositeOperation = 'destination-over'
  for (let radius = 2; radius <= 24; radius += 2) {
    for (let step = 0; step < 8; step += 1) {
      const angle = step * Math.PI / 4
      context.drawImage(core, Math.round(Math.cos(angle) * (radius + BLEED_INSET_PX)), Math.round(Math.sin(angle) * (radius + BLEED_INSET_PX)))
    }
  }
  context.fillStyle = fillColor
  context.fillRect(0, 0, canvas.width, canvas.height)
  if (holes) {
    context.globalCompositeOperation = 'destination-out'
    context.fillStyle = '#000'
    holes.forEach((hole) => {
      context.beginPath()
      hole.forEach(([x, y], index) => {
        const px = holes.map.bridgePx[0] + x / holes.map.mmPerPx
        const py = holes.map.bridgePx[1] - y / holes.map.mmPerPx
        if (index === 0) context.moveTo(px, py)
        else context.lineTo(px, py)
      })
      context.closePath()
      context.fill()
    })
  }
  context.globalCompositeOperation = 'source-over'
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 4
  return texture
}

/**
 * @param {object} geometry
 * @param {(x: number, y: number, z: number) => number[]} toUv
 */
function projectUv(geometry, toUv) {
  const position = geometry.attributes.position
  const uv = new Float32Array(position.count * 2)
  for (let index = 0; index < position.count; index += 1) {
    const [u, v] = toUv(position.getX(index), position.getY(index), position.getZ(index))
    uv[index * 2] = u
    uv[index * 2 + 1] = v
  }
  return uv
}

/**
 * Floor202 from the 3D model, coloured by projecting the product photographs.
 * @param {object} THREE
 * @param {Record<string, object>} model
 * @param {object} data
 * @param {string} color
 * @param {{front: object, temple: object}} textures
 * @returns {{group: object, front: object, dispose: Function}}
 */
function buildModelFrame(THREE, model, data, color, textures) {
  const entry = data.colors[color]
  const disposables = []
  const group = new THREE.Group()
  const opacity = ACETATE_OPACITY[color] ?? 1
  const acetate = (texture) => new THREE.MeshStandardMaterial({
    color: 0x8c8c8c,
    map: texture,
    emissive: 0xffffff,
    emissiveMap: texture,
    emissiveIntensity: 0.55,
    roughness: 0.22,
    metalness: 0,
    envMapIntensity: 0.7,
    alphaTest: 0.5,
    transparent: opacity < 1,
    opacity
  })

  const map = entry.front
  const front = model.front.clone()
  front.setAttribute('uv', new THREE.BufferAttribute(projectUv(front, (x, y) => [
    (map.bridgePx[0] + x / map.mmPerPx) / map.image[0],
    1 - (map.bridgePx[1] - y / map.mmPerPx) / map.image[1]
  ]), 2))
  const frontMaterial = acetate(textures.front)
  disposables.push(front, frontMaterial)
  const frontMesh = new THREE.Mesh(front, frontMaterial)
  group.add(frontMesh)

  const temple = entry.temple
  const outline = temple.outline
  let pxMin = Infinity
  let pxMax = -Infinity
  let pyMin = Infinity
  let pyMax = -Infinity
  outline.forEach(([px, py]) => {
    pxMin = Math.min(pxMin, px)
    pxMax = Math.max(pxMax, px)
    pyMin = Math.min(pyMin, py)
    pyMax = Math.max(pyMax, py)
  })
  const box = model.temples.boundingBox
  const temples = model.temples.clone()
  temples.setAttribute('uv', new THREE.BufferAttribute(projectUv(temples, (_x, y, z) => {
    const px = pxMin + (box.max.z - z) / (box.max.z - box.min.z) * (pxMax - pxMin)
    const py = pyMin + (box.max.y - y) / (box.max.y - box.min.y) * (pyMax - pyMin)
    return [px / temple.image[0], 1 - py / temple.image[1]]
  }), 2))
  const templeMaterial = acetate(textures.temple)
  disposables.push(temples, templeMaterial)
  const sides = { 1: [], '-1': [] }
  const index = temples.index.array
  const templePosition = temples.attributes.position
  for (let corner = 0; corner < index.length; corner += 3) {
    const x = templePosition.getX(index[corner]) + templePosition.getX(index[corner + 1]) + templePosition.getX(index[corner + 2])
    const bucket = sides[x >= 0 ? 1 : -1]
    bucket.push(index[corner], index[corner + 1], index[corner + 2])
  }
  const templeMeshes = [1, -1].map((side) => {
    const half = new THREE.BufferGeometry()
    Object.entries(temples.attributes).forEach(([name, attribute]) => half.setAttribute(name, attribute))
    half.setIndex(sides[side])
    disposables.push(half)
    const mesh = new THREE.Mesh(half, templeMaterial)
    mesh.userData.side = side
    group.add(mesh)
    return mesh
  })

  const lensMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 0.05,
    metalness: 0,
    envMapIntensity: 0.9,
    transparent: true,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
    depthWrite: false,
    side: THREE.DoubleSide
  })
  disposables.push(lensMaterial)
  const lenses = new THREE.Mesh(model.lenses, lensMaterial)
  lenses.renderOrder = 2
  group.add(lenses)

  const padMaterial = new THREE.MeshStandardMaterial({
    color: 0xf2efe8,
    roughness: 0.3,
    metalness: 0,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    envMapIntensity: 0.8
  })
  disposables.push(padMaterial)
  const pads = new THREE.Mesh(model.pads, padMaterial)
  pads.renderOrder = 1
  group.add(pads)

  return {
    group,
    front: frontMesh,
    temples: templeMeshes,
    dispose() {
      disposables.forEach((item) => item.dispose())
    }
  }
}

/**
 * A soft studio panorama for the acetate and lens reflections.
 * @param {object} THREE
 * @returns {object}
 */
function studioTexture(THREE) {
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 256
  const context = canvas.getContext('2d')
  const sky = context.createLinearGradient(0, 0, 0, 256)
  sky.addColorStop(0, '#fbf8f3')
  sky.addColorStop(0.45, '#cfc6bb')
  sky.addColorStop(0.55, '#7d7268')
  sky.addColorStop(1, '#2e2925')
  context.fillStyle = sky
  context.fillRect(0, 0, 512, 256)
  context.fillStyle = 'rgba(255, 255, 255, 0.95)'
  context.fillRect(200, 28, 112, 46)
  context.fillRect(40, 60, 70, 64)
  context.fillRect(402, 60, 70, 64)
  context.fillStyle = 'rgba(255, 246, 232, 0.6)'
  context.fillRect(0, 96, 512, 10)
  const texture = new THREE.CanvasTexture(canvas)
  texture.mapping = THREE.EquirectangularReflectionMapping
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

/**
 * @param {object} THREE
 * @param {number[][]} points
 * @param {number[][][]} holes
 * @returns {object}
 */
function makeShape(THREE, points, holes = []) {
  const shape = new THREE.Shape(points.map(([x, y]) => new THREE.Vector2(x, y)))
  holes.forEach((hole) => {
    shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))))
  })
  return shape
}

/**
 * Extrude UVs: caps sample the photograph, side walls use a flat colour.
 * @param {object} THREE
 * @param {(x: number, y: number) => number[]} toUv
 * @returns {object}
 */
function uvGenerator(THREE, toUv) {
  return {
    generateTopUV(_geometry, vertices, a, b, c) {
      return [a, b, c].map((index) => {
        const [u, v] = toUv(vertices[index * 3], vertices[index * 3 + 1])
        return new THREE.Vector2(u, v)
      })
    },
    generateSideWallUV() {
      return [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()]
    }
  }
}

/**
 * Builds the try-on frame for one colour.
 * @param {object} THREE
 * @param {object} data Frame shape (the e8_vto_frame shape field).
 * @param {string} color
 * @param {{front: object, temple: object}} textures
 * @returns {{group: object, front: object, dispose: Function}}
 */
function buildFrame(THREE, data, color, textures) {
  const front = data.front
  const entry = data.colors[color]
  const disposables = []
  const group = new THREE.Group()

  const capMaterial = (texture) => new THREE.MeshStandardMaterial({
    color: 0x000000,
    map: texture,
    emissive: 0xffffff,
    emissiveMap: texture,
    roughness: 0.3,
    metalness: 0,
    alphaTest: 0.5,
    envMapIntensity: 0.55,
    side: THREE.DoubleSide
  })
  const sideMaterial = (hex) => {
    const base = new THREE.Color(hex)
    return new THREE.MeshStandardMaterial({
      color: base.clone().multiplyScalar(0.7),
      emissive: base.clone().multiplyScalar(0.55),
      roughness: 0.38,
      metalness: 0,
      envMapIntensity: 0.5,
      side: THREE.DoubleSide
    })
  }

  const map = entry.front
  const frontUv = (x, y) => [
    (map.bridgePx[0] + x / map.mmPerPx) / map.image[0],
    1 - (map.bridgePx[1] - y / map.mmPerPx) / map.image[1]
  ]
  const frontMaterials = [capMaterial(textures.front), sideMaterial(map.sideColor)]
  disposables.push(...frontMaterials)
  const frontGroup = new THREE.Group()
  front.shapes.forEach((item) => {
    const geometry = new THREE.ExtrudeGeometry(makeShape(THREE, item.outer, item.holes), {
      depth: FRONT_DEPTH_MM,
      bevelEnabled: true,
      bevelThickness: 0.45,
      bevelSize: 0.35,
      bevelSegments: 2,
      curveSegments: 1,
      UVGenerator: uvGenerator(THREE, frontUv)
    })
    geometry.translate(0, 0, -FRONT_DEPTH_MM)
    disposables.push(geometry)
    frontGroup.add(new THREE.Mesh(geometry, frontMaterials))
  })
  group.add(frontGroup)

  const lensMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 0.05,
    metalness: 0,
    envMapIntensity: 1.5,
    transparent: true,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
    depthWrite: false,
    side: THREE.DoubleSide
  })
  disposables.push(lensMaterial)
  front.lenses.forEach((outline) => {
    const geometry = new THREE.ShapeGeometry(makeShape(THREE, outline), 1)
    const center = outline.reduce((sum, [x, y]) => [sum[0] + x / outline.length, sum[1] + y / outline.length], [0, 0])
    const position = geometry.attributes.position
    const normal = geometry.attributes.normal
    for (let index = 0; index < position.count; index += 1) {
      const dx = position.getX(index) - center[0]
      const dy = position.getY(index) - center[1]
      position.setZ(index, -1 - (dx * dx + dy * dy) / (2 * LENS_BASE_RADIUS_MM))
      const n = new THREE.Vector3(dx, dy, LENS_BASE_RADIUS_MM).normalize()
      normal.setXYZ(index, n.x, n.y, n.z)
    }
    position.needsUpdate = true
    normal.needsUpdate = true
    disposables.push(geometry)
    const mesh = new THREE.Mesh(geometry, lensMaterial)
    mesh.renderOrder = 2
    group.add(mesh)
  })

  const temple = entry.temple
  const templeUv = (x, y) => [
    (temple.hingePx[0] + x / temple.mmPerPx) / temple.image[0],
    1 - (temple.hingePx[1] - y / temple.mmPerPx) / temple.image[1]
  ]
  const outline = temple.outline.map(([px, py]) => [
    (px - temple.hingePx[0]) * temple.mmPerPx,
    (temple.hingePx[1] - py) * temple.mmPerPx
  ])
  const templeGeometry = new THREE.ExtrudeGeometry(makeShape(THREE, outline), {
    depth: TEMPLE_DEPTH_MM,
    bevelEnabled: true,
    bevelThickness: 0.25,
    bevelSize: 0.2,
    bevelSegments: 1,
    curveSegments: 1,
    UVGenerator: uvGenerator(THREE, templeUv)
  })
  templeGeometry.translate(0, 0, -TEMPLE_DEPTH_MM / 2)
  templeGeometry.rotateY(Math.PI / 2)
  disposables.push(templeGeometry)
  const templeMaterials = [capMaterial(textures.temple), sideMaterial(temple.sideColor)]
  disposables.push(...templeMaterials)
  const [hingeX, hingeY] = front.hinge
  const templeGroups = [1, -1].map((side) => {
    const mirror = new THREE.Group()
    mirror.scale.x = side
    mirror.userData.side = side
    const hinge = new THREE.Group()
    hinge.position.set(hingeX - TEMPLE_DEPTH_MM / 2, hingeY, -FRONT_DEPTH_MM + 0.6)
    hinge.rotation.y = -TEMPLE_SPLAY
    hinge.add(new THREE.Mesh(templeGeometry, templeMaterials))
    mirror.add(hinge)
    group.add(mirror)
    return mirror
  })

  return {
    group,
    front: frontGroup,
    temples: templeGroups,
    dispose() {
      disposables.forEach((item) => item.dispose())
    }
  }
}

class TryOnSession {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root
    this.dialog = root.querySelector('.js-vtoDialog')
    this.openButton = root.querySelector('.js-vtoOpen')
    this.closeButton = root.querySelector('.js-vtoClose')
    this.video = root.querySelector('.js-vtoVideo')
    this.frameCanvas = root.querySelector('.js-vtoFrame')
    this.glassCanvas = root.querySelector('.js-vtoGlasses')
    this.occludeCanvas = root.querySelector('.js-vtoOcclude')
    this.status = root.querySelector('.js-vtoStatus')
    this.result = root.querySelector('.js-vtoResult')
    this.numbers = root.querySelector('.js-vtoNumbers')
    this.pdInput = root.querySelector('.js-vtoPd')
    this.positionInput = root.querySelector('.js-vtoPosition')
    this.productLabel = root.querySelector('.js-vtoProduct')
    this.productLink = root.querySelector('.js-vtoProductLink')
    this.copy = JSON.parse(root.querySelector('.js-vtoCopy').textContent || '{}')
    this.frameNodes = new Map([...root.querySelectorAll('.js-vtoFrameData')].map((node) => [node.dataset.frame, node]))
    this.frameCache = new Map()
    this.frameKey = ''
    this.frameToken = 0
    this.data = null
    this.model = null
    this.textures = new Map()
    if (this.frameNodes.size) {
      this.pageColor = ''
      this.useFrame(this.frameNodes.has(root.dataset.vtoFrame) ? root.dataset.vtoFrame : this.frameNodes.keys().next().value)
    } else {
      const colorsNode = root.querySelector('.js-vtoColors')
      this.colors = colorsNode ? JSON.parse(colorsNode.textContent || '{}') : {}
      this.pageColor = root.dataset.vtoColor
      this.color = this.pageColor
      this.dataUrl = root.dataset.vtoData
      this.spec = readSpec(root.dataset)
      const shapeNode = root.querySelector('.js-vtoShape')
      this.inlineShape = shapeNode ? JSON.parse(shapeNode.textContent || 'null') : null
      this.modelUrl = root.dataset.vtoModel
    }
    this.three = null
    this.scene = null
    this.camera = null
    this.renderer = null
    this.anchor = null
    this.frameRoot = null
    this.frame = null
    this.landmarker = null
    this.segmenter = null
    this.stream = null
    this.loopId = 0
    this.running = false
    this.mode = 'camera'
    this.detectMode = 'VIDEO'
    this.segmentMode = 'VIDEO'
    this.lastStamp = 0
    this.missed = 0
    this.frameCount = 0
    this.hairCanvas = document.createElement('canvas')
    this.hairScratch = document.createElement('canvas')
    this.sourceCanvas = document.createElement('canvas')
    this.crop = null
    this.viewRect = null
    this.filters = null
    this.debugYaw = null
    this.lastFace = null
    this.viewportWidth = 0
    this.onKeydown = this.onKeydown.bind(this)
    this.onResize = this.onResize.bind(this)
    this.tick = this.tick.bind(this)
  }

  /**
   * @returns {Promise<void>}
   */
  async open() {
    if (!this.dialog || !this.colors[this.color]) return
    this.dialog.hidden = false
    document.querySelectorAll('.js-vtoDialog').forEach((node) => {
      if (node !== this.dialog && node.dataset.vtoId === this.dialog.dataset.vtoId) node.remove()
    })
    document.body.appendChild(this.dialog)
    document.body.style.overflow = 'hidden'
    document.body.classList.add('e8-vto-open')
    if (this.fitStage()) this.applyStage()
    if (this.closeButton) this.closeButton.focus()
    document.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)
    this.setStatus(this.copy.preparing)
    this.syncColorButtons()
    try {
      const stored = parsePd(localStorage.getItem(STORAGE_KEY) || '')
      if (stored.custom && this.pdInput) this.pdInput.value = String(stored.pd)
    } catch (error) {
      console.error(error)
    }
    const streamRequest = this.requestStream()
    streamRequest.catch(() => {})
    try {
      await Promise.all([this.ensureModels(), this.ensureScene()])
    } catch (error) {
      console.error(error)
      this.setStatus(this.copy.error)
      streamRequest.then((stream) => stream && stream.getTracks().forEach((track) => track.stop()), () => {})
      return
    }
    try {
      await this.startCamera(streamRequest)
    } catch (error) {
      console.error(error)
      this.setStatus(this.copy.cameraError)
    }
  }

  close() {
    this.running = false
    if (this.loopId) cancelAnimationFrame(this.loopId)
    this.loopId = 0
    this.stopStream()
    if (this.dialog) this.dialog.hidden = true
    document.body.style.overflow = ''
    document.body.classList.remove('e8-vto-open')
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    if (this.openButton) this.openButton.focus()
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }
  }

  /**
   * @param {string} message
   */
  setStatus(message) {
    if (this.status) this.status.textContent = message || ''
  }

  /**
   * @returns {Promise<void>}
   */
  async ensureModels() {
    if (this.landmarker) return
    const vision = await import(`${VISION_PKG}/vision_bundle.mjs`)
    const files = await vision.FilesetResolver.forVisionTasks(WASM_BASE)
    const options = (delegate) => ({
      baseOptions: { modelAssetPath: FACE_MODEL, delegate },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFacialTransformationMatrixes: true
    })
    try {
      this.landmarker = await vision.FaceLandmarker.createFromOptions(files, options('GPU'))
    } catch (error) {
      console.error(error)
      this.landmarker = await vision.FaceLandmarker.createFromOptions(files, options('CPU'))
    }
    this.detectMode = 'VIDEO'
    this.hairReady = this.loadHair(vision, files)
  }

  /**
   * Hair over the temples is optional; the try-on continues without it.
   * @param {object} vision
   * @param {object} files
   * @returns {Promise<void>}
   */
  async loadHair(vision, files) {
    const options = (delegate) => ({
      baseOptions: { modelAssetPath: HAIR_MODEL, delegate },
      runningMode: 'VIDEO',
      outputCategoryMask: true,
      outputConfidenceMasks: false
    })
    try {
      this.segmenter = await vision.ImageSegmenter.createFromOptions(files, options('GPU'))
    } catch (error) {
      console.error(error)
      try {
        this.segmenter = await vision.ImageSegmenter.createFromOptions(files, options('CPU'))
      } catch (cpuError) {
        console.error(cpuError)
        this.segmenter = null
      }
    }
    this.segmentMode = 'VIDEO'
  }

  /**
   * @returns {Promise<void>}
   */
  async ensureScene() {
    if (this.renderer) {
      await this.ensureFrameAssets()
      await this.setColor(this.color)
      return
    }
    const THREE = await loadThree()
    this.three = THREE
    await this.ensureFrameAssets()
    this.frameCanvas.width = STAGE_WIDTH
    this.frameCanvas.height = stageSize.height
    this.occludeCanvas.width = STAGE_WIDTH
    this.occludeCanvas.height = stageSize.height
    const renderer = new THREE.WebGLRenderer({
      canvas: this.glassCanvas,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    renderer.setSize(STAGE_WIDTH, stageSize.height, false)
    renderer.setClearColor(0x000000, 0)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer = renderer
    this.scene = new THREE.Scene()
    const pmrem = new THREE.PMREMGenerator(renderer)
    const studio = studioTexture(THREE)
    this.scene.environment = pmrem.fromEquirectangular(studio).texture
    studio.dispose()
    pmrem.dispose()
    const key = new THREE.DirectionalLight(0xffffff, 1.1)
    key.position.set(0.4, 1, 1.2)
    this.scene.add(key)
    this.scene.add(new THREE.HemisphereLight(0xfff8ef, 0x3a312b, 0.5))
    this.camera = new THREE.PerspectiveCamera(30, STAGE_WIDTH / stageSize.height, 10, 4000)
    this.anchor = new THREE.Group()
    this.anchor.visible = false
    this.scene.add(this.anchor)
    const headGeometry = new THREE.SphereGeometry(1, 48, 32)
    const headPosition = headGeometry.attributes.position
    for (let index = 0; index < headPosition.count; index += 1) {
      const squarish = (value) => Math.sign(value) * Math.pow(Math.abs(value), 0.55)
      headPosition.setX(index, squarish(headPosition.getX(index)))
      headPosition.setZ(index, squarish(headPosition.getZ(index)))
    }
    const head = new THREE.Mesh(headGeometry, new THREE.MeshBasicMaterial({ colorWrite: false }))
    head.renderOrder = -1
    head.scale.set(66, 108, 92)
    head.position.set(0, 12, -2 - 92)
    this.head = head
    this.anchor.add(head)
    const earGeometry = new THREE.SphereGeometry(1, 24, 16)
    this.ears = [1, -1].map((side) => {
      const ear = new THREE.Mesh(earGeometry, head.material)
      ear.renderOrder = -1
      ear.scale.set(11, 40, 30)
      ear.position.set(side * 74, -20, -104)
      ear.userData.side = side
      this.anchor.add(ear)
      return ear
    })
    this.frameRoot = new THREE.Group()
    this.frameRoot.position.set(0, 0, VERTEX_DISTANCE_MM)
    this.anchor.add(this.frameRoot)
    await this.setColor(this.color)
  }

  /**
   * Loads the current frame's shapes and 3D model once.
   * @returns {Promise<void>}
   */
  async ensureFrameAssets() {
    if (this.data) return
    const key = this.frameKey
    const [data, model] = await Promise.all([
      this.inlineShape ? Promise.resolve(this.inlineShape) : loadShapes(this.dataUrl),
      this.modelUrl
        ? loadModel(this.three, this.modelUrl).catch((error) => {
            console.error(error)
            return null
          })
        : null
    ])
    if (key) this.frameCache.set(key, { data, model, textures: this.textures })
    if (key !== this.frameKey) return
    this.data = data
    this.model = model
  }

  /**
   * Switches to a frame of the try-on page without loading it.
   * @param {string} key Frame metaobject handle.
   */
  useFrame(key) {
    const node = this.frameNodes.get(key)
    if (!node) return
    if (!node.e8Config) {
      const colorsNode = node.querySelector('.js-vtoColors')
      const shapeNode = node.querySelector('.js-vtoShape')
      node.e8Config = {
        colors: colorsNode ? JSON.parse(colorsNode.textContent || '{}') : {},
        shape: shapeNode ? JSON.parse(shapeNode.textContent || 'null') : null
      }
    }
    const cached = this.frameCache.get(key)
    this.frameKey = key
    this.colors = node.e8Config.colors
    this.inlineShape = node.e8Config.shape
    this.spec = readSpec(node.dataset)
    this.modelUrl = node.dataset.vtoModel
    this.dataUrl = node.dataset.vtoData
    this.data = cached ? cached.data : null
    this.model = cached ? cached.model : null
    this.textures = cached ? cached.textures : new Map()
    this.color = this.colors[node.dataset.vtoColor] ? node.dataset.vtoColor : Object.keys(this.colors)[0]
  }

  /**
   * @param {string} key Frame metaobject handle.
   * @returns {Promise<void>}
   */
  async setFrame(key) {
    if (key === this.frameKey || !this.frameNodes.has(key)) return
    const token = ++this.frameToken
    this.useFrame(key)
    this.syncColorButtons()
    if (!this.renderer) return
    this.setStatus(this.copy.preparing)
    await this.ensureFrameAssets()
    if (token !== this.frameToken) return
    await this.setColor(this.color)
    this.setStatus(this.lastFace ? '' : this.mode === 'camera' ? this.copy.searching : '')
  }

  /**
   * @param {string} color
   * @returns {Promise<void>}
   */
  async setColor(color) {
    const entry = this.colors[color]
    if (!entry || !this.data || !this.data.colors[color]) return
    const THREE = this.three
    if (!this.textures.has(color)) {
      const frameKey = this.frameKey
      const [frontImage, templeImage] = await Promise.all([loadImage(entry.front), loadImage(entry.temple)])
      if (frameKey !== this.frameKey) return
      const mapping = this.data.colors[color]
      const textures = this.model
        ? {
            front: bleedTexture(
              THREE,
              frontImage,
              mapping.front.sideColor,
              Object.assign(this.data.front.shapes.flatMap((shape) => shape.holes), { map: mapping.front })
            ),
            temple: bleedTexture(THREE, templeImage, mapping.temple.sideColor)
          }
        : {
            front: new THREE.Texture(frontImage),
            temple: new THREE.Texture(templeImage)
          }
      Object.values(textures).forEach((texture) => {
        texture.colorSpace = THREE.SRGBColorSpace
        texture.anisotropy = 4
        texture.needsUpdate = true
      })
      this.textures.set(color, textures)
    }
    if (this.frame) {
      this.frameRoot.remove(this.frame.group)
      this.frame.dispose()
    }
    this.frame = this.model
      ? buildModelFrame(THREE, this.model, this.data, color, this.textures.get(color))
      : buildFrame(THREE, this.data, color, this.textures.get(color))
    this.frameRoot.add(this.frame.group)
    this.color = color
    this.syncColorButtons()
    if (this.lastFace) this.renderFace(this.lastFace.landmarks, this.lastFace.matrix)
    else this.renderer.render(this.scene, this.camera)
  }

  syncColorButtons() {
    const entry = this.colors[this.color]
    if (this.productLabel && entry) this.productLabel.textContent = entry.title || entry.label
    if (this.productLink && entry) {
      this.productLink.hidden = this.color === this.pageColor
      this.productLink.href = entry.url
    }
    if (!this.dialog) return
    this.dialog.querySelectorAll('.js-vtoFrameButton').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.frame === this.frameKey))
    })
    this.dialog.querySelectorAll('.js-vtoColorGroup').forEach((group) => {
      group.hidden = group.dataset.frame !== this.frameKey
    })
    this.dialog.querySelectorAll('.js-vtoColor').forEach((button) => {
      const group = button.closest('.js-vtoColorGroup')
      const current = !group || group.dataset.frame === this.frameKey
      button.setAttribute('aria-pressed', String(current && button.dataset.color === this.color))
    })
  }

  /**
   * @returns {Promise<void>}
   */
  /**
   * Asks for the camera straight from the click, so the browser prompt
   * appears while the face models are still loading.
   * @returns {Promise<MediaStream|null>}
   */
  requestStream() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return Promise.resolve(null)
    return navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }
    })
  }

  /**
   * @param {Promise<MediaStream|null>} [streamRequest]
   * @returns {Promise<void>}
   */
  async startCamera(streamRequest) {
    this.stopStream()
    this.mode = 'camera'
    this.crop = null
    this.filters = null
    const stream = await (streamRequest || this.requestStream())
    if (!stream) {
      this.setStatus(this.copy.cameraError)
      return
    }
    if (this.dialog.hidden) {
      stream.getTracks().forEach((track) => track.stop())
      return
    }
    if (this.detectMode !== 'VIDEO') {
      await this.landmarker.setOptions({ runningMode: 'VIDEO' })
      this.detectMode = 'VIDEO'
    }
    if (this.segmenter && this.segmentMode !== 'VIDEO') {
      await this.segmenter.setOptions({ runningMode: 'VIDEO' })
      this.segmentMode = 'VIDEO'
    }
    this.stream = stream
    this.video.srcObject = this.stream
    await this.video.play()
    this.running = true
    this.setStatus(this.copy.searching)
    this.loopId = requestAnimationFrame(this.tick)
  }

  stopStream() {
    if (!this.stream) return
    this.stream.getTracks().forEach((track) => track.stop())
    this.stream = null
    if (this.video) this.video.srcObject = null
  }

  /**
   * Still photos are not mirrored.
   * @param {File} file
   * @returns {Promise<void>}
   */
  async usePhoto(file) {
    this.setStatus(this.copy.preparing || '')
    this.running = false
    if (this.loopId) cancelAnimationFrame(this.loopId)
    this.stopStream()
    this.mode = 'photo'
    this.filters = null
    const url = URL.createObjectURL(file)
    try {
      const image = await loadImage(url)
      await Promise.all([this.ensureModels(), this.ensureScene()])
      if (this.detectMode !== 'IMAGE') {
        await this.landmarker.setOptions({ runningMode: 'IMAGE' })
        this.detectMode = 'IMAGE'
      }
      if (this.hairReady) await this.hairReady
      if (this.segmenter && this.segmentMode !== 'IMAGE') {
        await this.segmenter.setOptions({ runningMode: 'IMAGE' })
        this.segmentMode = 'IMAGE'
      }
      this.crop = null
      this.drawSource(image, false)
      this.present()
      this.detectAndRender()
    } catch (error) {
      console.error(error)
      this.setStatus(this.copy.error)
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  /**
   * Draws the camera or photo into an offscreen frame. The stage shows a face
   * crop of it, so the frame stays large enough to compare.
   * @param {CanvasImageSource} source
   * @param {boolean} mirror
   * @returns {HTMLCanvasElement|null}
   */
  drawSource(source, mirror) {
    const sourceWidth = source.videoWidth || source.width
    const sourceHeight = source.videoHeight || source.height
    if (!sourceWidth || !sourceHeight) return null
    const scale = Math.min(1, 960 / Math.max(sourceWidth, sourceHeight))
    const width = Math.max(2, Math.round(sourceWidth * scale))
    const height = Math.max(2, Math.round(sourceHeight * scale))
    const canvas = this.sourceCanvas
    if (canvas.width !== width) canvas.width = width
    if (canvas.height !== height) canvas.height = height
    const context = canvas.getContext('2d', { willReadFrequently: true })
    context.save()
    if (mirror) {
      context.translate(width, 0)
      context.scale(-1, 1)
    }
    context.drawImage(source, 0, 0, width, height)
    context.restore()
    return canvas
  }

  /**
   * Before a face is found, the camera fills the stage; photos are shown whole.
   * @returns {{x: number, y: number, w: number, h: number}}
   */
  activeCrop() {
    if (this.crop) return this.crop
    const source = this.sourceCanvas
    if (this.mode !== 'camera' || !source.width || !source.height) {
      return { x: 0, y: 0, w: source.width, h: source.height }
    }
    const aspect = STAGE_WIDTH / stageSize.height
    const w = Math.min(source.width, source.height * aspect)
    const h = w / aspect
    return { x: (source.width - w) / 2, y: (source.height - h) / 2, w, h }
  }

  /**
   * On phones the stage is the whole screen, so it takes the screen's shape and
   * the face sits above the bottom controls.
   * @returns {boolean} Whether the stage changed size.
   */
  fitStage() {
    const stage = this.frameCanvas.parentElement
    const full = window.matchMedia(FULL_SCREEN_QUERY).matches && stage && stage.clientWidth > 0 && stage.clientHeight > 0
    const aspect = full ? clamp(stage.clientWidth / stage.clientHeight, 0.4, 1) : DESKTOP_ASPECT
    const height = Math.round(STAGE_WIDTH / aspect)
    this.viewportWidth = window.innerWidth
    stageSize.faceY = full ? 0.4 : 0.46
    if (height === stageSize.height) return false
    stageSize.height = height
    return true
  }

  applyStage() {
    this.frameCanvas.height = stageSize.height
    this.occludeCanvas.height = stageSize.height
    if (!this.renderer) return
    this.renderer.setSize(STAGE_WIDTH, stageSize.height, false)
    this.camera.aspect = STAGE_WIDTH / stageSize.height
    this.camera.updateProjectionMatrix()
    this.crop = null
    this.viewRect = null
    this.filters = null
    if (this.mode !== 'photo' || !this.sourceCanvas.width || !this.landmarker) return
    try {
      this.present()
      this.detectAndRender()
    } catch (error) {
      console.error(error)
      this.setStatus(this.copy.error)
    }
  }

  /**
   * The on-screen keyboard changes only the height, so the stage is refitted
   * when the width changes (rotation).
   */
  onResize() {
    if (window.innerWidth === this.viewportWidth) return
    if (this.fitStage()) this.applyStage()
  }

  /**
   * @param {string} name
   */
  selectPanel(name) {
    if (!this.dialog) return
    this.dialog.querySelectorAll('.js-vtoTab').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.panel === name))
    })
    this.dialog.querySelectorAll('.js-vtoSection').forEach((section) => {
      if (section.dataset.panel === name) section.dataset.active = 'true'
      else delete section.dataset.active
    })
  }

  /**
   * Fits the current face crop into the stage.
   */
  present() {
    const source = this.sourceCanvas
    const crop = this.activeCrop()
    const fit = Math.min(STAGE_WIDTH / crop.w, stageSize.height / crop.h)
    const dw = crop.w * fit
    const dh = crop.h * fit
    const dx = (STAGE_WIDTH - dw) / 2
    const dy = (stageSize.height - dh) / 2
    this.viewRect = { dx, dy, dw, dh }
    const context = this.frameCanvas.getContext('2d', { willReadFrequently: true })
    context.clearRect(0, 0, STAGE_WIDTH, stageSize.height)
    context.drawImage(source, crop.x, crop.y, crop.w, crop.h, dx, dy, dw, dh)
  }

  /**
   * Stage-normalised landmark to source-image pixels.
   * @param {number} normX
   * @param {number} normY
   * @returns {{x: number, y: number}}
   */
  sourcePoint(normX, normY) {
    const crop = this.activeCrop()
    const rect = this.viewRect || { dx: 0, dy: 0, dw: STAGE_WIDTH, dh: stageSize.height }
    const u = (normX * STAGE_WIDTH - rect.dx) / rect.dw
    const v = (normY * stageSize.height - rect.dy) / rect.dh
    return { x: crop.x + u * crop.w, y: crop.y + v * crop.h }
  }

  /**
   * Source-image pixels to a stage-normalised point.
   * @param {number} sourceX
   * @param {number} sourceY
   * @returns {{x: number, y: number}}
   */
  stagePoint(sourceX, sourceY) {
    const crop = this.activeCrop()
    const rect = this.viewRect || { dx: 0, dy: 0, dw: STAGE_WIDTH, dh: stageSize.height }
    const u = (sourceX - crop.x) / crop.w
    const v = (sourceY - crop.y) / crop.h
    return { x: (rect.dx + u * rect.dw) / STAGE_WIDTH, y: (rect.dy + v * rect.dh) / stageSize.height }
  }

  /**
   * Keeps the face near 58% of the stage width.
   * @param {object[]} marks Source-image pixel landmarks.
   */
  refineCrop(marks) {
    const source = this.sourceCanvas
    if (!marks[234] || !marks[454] || !marks[10] || !marks[152]) return
    const faceW = Math.hypot(marks[454].x - marks[234].x, marks[454].y - marks[234].y)
    const faceH = Math.hypot(marks[152].x - marks[10].x, marks[152].y - marks[10].y)
    if (faceW < 24 || faceH < 24) return
    const aspect = STAGE_WIDTH / stageSize.height
    let cropW = faceW / 0.58
    let cropH = cropW / aspect
    if (cropH < faceH / 0.7) {
      cropH = faceH / 0.7
      cropW = cropH * aspect
    }
    cropW = Math.min(cropW, source.width)
    cropH = Math.min(cropH, source.height)
    if (cropW / cropH > aspect) cropW = cropH * aspect
    else cropH = cropW / aspect
    const centerX = (marks[234].x + marks[454].x) / 2
    const centerY = (marks[10].y + marks[152].y) / 2
    const next = {
      x: clamp(centerX - cropW / 2, 0, Math.max(0, source.width - cropW)),
      y: clamp(centerY - cropH * stageSize.faceY, 0, Math.max(0, source.height - cropH)),
      w: cropW,
      h: cropH
    }
    if (!this.crop) {
      this.crop = next
      return
    }
    const crop = this.crop
    const drift = Math.max(Math.abs(next.x - crop.x), Math.abs(next.y - crop.y)) / crop.w
    const zoom = Math.abs(next.w / crop.w - 1)
    if (!this.cropMoving && drift < 0.06 && zoom < 0.08) return
    this.cropMoving = drift > 0.01 || zoom > 0.015
    const blend = 0.06
    crop.x += (next.x - crop.x) * blend
    crop.y += (next.y - crop.y) * blend
    crop.w += (next.w - crop.w) * blend
    crop.h += (next.h - crop.h) * blend
  }

  /**
   * @param {DOMHighResTimeStamp} time
   */
  tick(time) {
    if (!this.running) return
    this.loopId = requestAnimationFrame(this.tick)
    if (this.video.readyState < 2) return
    try {
      if (!this.drawSource(this.video, true)) return
      this.present()
      this.detectAndRender(time)
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * @param {number} [time]
   */
  detectAndRender(time) {
    let stamp = typeof time === 'number' ? time : performance.now()
    if (stamp <= this.lastStamp) stamp = this.lastStamp + 1
    this.lastStamp = stamp
    const faces = this.detectMode === 'IMAGE'
      ? this.landmarker.detect(this.frameCanvas)
      : this.landmarker.detectForVideo(this.frameCanvas, stamp)
    this.frameCount += 1
    if (this.segmenter && this.detectMode === 'VIDEO' && this.frameCount % 3 === 0) {
      this.segmenter.segmentForVideo(this.frameCanvas, stamp, (result) => {
        if (result.categoryMask) this.paintHairMask(result.categoryMask)
      })
    }
    let landmarks = faces.faceLandmarks && faces.faceLandmarks[0]
    if (!landmarks) {
      this.missed += 1
      if (this.mode === 'photo' || this.missed > 8) {
        this.setStatus(this.copy.searching)
        this.clearGlasses()
      }
      return
    }
    this.missed = 0
    this.setStatus('')
    const matrix = faces.facialTransformationMatrixes && faces.facialTransformationMatrixes[0]
    const sourceMarks = landmarks.map((mark) => {
      const point = this.sourcePoint(mark.x, mark.y)
      return { x: point.x, y: point.y, z: mark.z || 0 }
    })
    if (!this.crop) {
      this.refineCrop(sourceMarks)
      if (this.crop) {
        this.present()
        landmarks = sourceMarks.map((mark) => {
          const point = this.stagePoint(mark.x, mark.y)
          return { x: point.x, y: point.y, z: mark.z }
        })
        if (this.mode === 'photo') this.segmentStill()
      }
      this.renderFace(landmarks, matrix && matrix.data)
      return
    }
    this.renderFace(landmarks, matrix && matrix.data)
    this.refineCrop(sourceMarks)
  }

  /**
   * Still photos run the image segmenter once.
   */
  segmentStill() {
    if (!this.segmenter || this.segmentMode !== 'IMAGE') return
    try {
      this.segmenter.segment(this.frameCanvas, (result) => {
        if (!result || !result.categoryMask) return
        this.paintHairMask(result.categoryMask)
      })
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * @param {object} mask
   */
  paintHairMask(mask) {
    const pixels = mask.getAsUint8Array()
    const width = mask.width
    const height = mask.height
    if (!width || !height) return
    const image = new ImageData(width, height)
    for (let index = 0; index < width * height; index += 1) {
      const offset = index * 4
      image.data[offset] = 255
      image.data[offset + 1] = 255
      image.data[offset + 2] = 255
      image.data[offset + 3] = pixels[index] === HAIR_CATEGORY ? 255 : 0
    }
    this.hairCanvas.width = width
    this.hairCanvas.height = height
    this.hairCanvas.getContext('2d').putImageData(image, 0, 0)
  }

  clearGlasses() {
    this.lastFace = null
    if (this.anchor) this.anchor.visible = false
    if (this.renderer) this.renderer.render(this.scene, this.camera)
    const context = this.occludeCanvas.getContext('2d')
    context.clearRect(0, 0, STAGE_WIDTH, stageSize.height)
    if (this.result) this.result.textContent = ''
    if (this.numbers) this.numbers.textContent = ''
  }

  /**
   * @returns {{pd: number, invalid: boolean}}
   */
  currentPd() {
    const parsed = parsePd(this.pdInput ? this.pdInput.value : '')
    if (parsed.invalid) return parsed
    try {
      if (parsed.custom) localStorage.setItem(STORAGE_KEY, String(parsed.pd))
      else localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.error(error)
    }
    return parsed
  }

  /**
   * Places the frame on the face and renders one image.
   * @param {object[]} landmarks Stage-normalised landmarks.
   * @param {number[]|undefined} matrix
   */
  renderFace(landmarks, matrix) {
    if (!this.renderer || !this.frame) return
    this.lastFace = { landmarks, matrix }
    const THREE = this.three
    const pdState = this.currentPd()
    if (pdState.invalid) {
      this.setStatus(this.copy.pdInvalid)
      if (this.result) this.result.textContent = ''
      if (this.numbers) this.numbers.textContent = ''
      return
    }
    if (this.status && this.status.textContent === this.copy.pdInvalid) this.setStatus('')
    const point = (index) => ({ x: landmarks[index].x * STAGE_WIDTH, y: landmarks[index].y * stageSize.height })
    const leftIris = point(468)
    const rightIris = point(473)
    const irisPx = Math.hypot(rightIris.x - leftIris.x, rightIris.y - leftIris.y)
    if (irisPx < 8) return
    const leftCorner = point(33)
    const rightCorner = point(263)
    const cornerPx = Math.hypot(rightCorner.x - leftCorner.x, rightCorner.y - leftCorner.y)

    const rotation = new THREE.Quaternion()
    if (matrix && matrix.length >= 16) {
      const m = new THREE.Matrix4().fromArray(matrix)
      const position = new THREE.Vector3()
      const scale = new THREE.Vector3()
      m.decompose(position, rotation, scale)
    }
    if (this.debugYaw !== null) {
      rotation.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), this.debugYaw))
    }
    const live = this.mode === 'camera'
    if (!live || !this.filters) {
      this.filters = {
        x: new OneEuro(1.2, 0.012),
        y: new OneEuro(1.2, 0.012),
        cornerPx: new OneEuro(0.8, 0.02),
        irisRatio: new OneEuro(0.15, 0),
        rotation: new OneEuroRotation(THREE, 0.9, 0.8)
      }
    }
    const time = performance.now() / 1000
    const filters = this.filters
    const steadyRotation = filters.rotation.filter(rotation, time)
    const axisX = new THREE.Vector3(1, 0, 0).applyQuaternion(steadyRotation)
    const foreshortening = clamp(Math.hypot(axisX.x, axisX.y), 0.45, 1)
    const zoom = this.viewRect && this.crop ? this.viewRect.dw / this.crop.w : 1
    const irisRatio = cornerPx > 8 ? filters.irisRatio.filter(irisPx / cornerPx, time) : 0
    const steadyIrisPx = irisRatio ? irisRatio * filters.cornerPx.filter(cornerPx / zoom, time) * zoom : irisPx
    const pxPerMm = steadyIrisPx / (pdState.pd * foreshortening)
    const euler = new THREE.Euler().setFromQuaternion(steadyRotation, 'YXZ')
    const mid = this.sourcePoint((leftIris.x + rightIris.x) / 2 / STAGE_WIDTH, (leftIris.y + rightIris.y) / 2 / stageSize.height)
    const steadyMid = this.stagePoint(filters.x.filter(mid.x, time), filters.y.filter(mid.y, time))
    const view = {
      x: steadyMid.x * STAGE_WIDTH,
      y: steadyMid.y * stageSize.height,
      pxPerMm,
      rotation: steadyRotation
    }

    const fov = 2 * Math.atan(stageSize.height / 2 / (view.pxPerMm * VIEW_DISTANCE_MM)) * 180 / Math.PI
    this.camera.fov = fov
    this.camera.updateProjectionMatrix()
    this.anchor.position.set(
      (view.x - STAGE_WIDTH / 2) / view.pxPerMm,
      -(view.y - stageSize.height / 2) / view.pxPerMm,
      -VIEW_DISTANCE_MM
    )
    this.anchor.quaternion.copy(view.rotation)

    const noseWidth = Math.hypot(point(98).x - point(327).x, point(98).y - point(327).y) / pxPerMm
    const noseDrop = noseWidth > this.spec.bridge ? Math.min(4, (noseWidth - this.spec.bridge) * 0.35) : 0
    const userShift = this.positionInput ? Number(this.positionInput.value) || 0 : 0
    this.frameRoot.position.y = -1 - noseDrop + userShift

    const faceWidthPx = Math.hypot(point(454).x - point(234).x, point(454).y - point(234).y)
    const faceWidth = faceWidthPx / pxPerMm
    if (Math.abs(euler.y) < 0.22 && faceWidth > 100 && faceWidth < 200) {
      this.head.scale.x = clamp(faceWidth / 2 - 3, 58, 68)
      this.ears.forEach((ear) => {
        ear.position.x = ear.userData.side * (this.head.scale.x + 8)
      })
    }

    this.hideFarTemple()
    this.anchor.visible = true
    this.renderer.render(this.scene, this.camera)
    this.drawOcclusion()
    this.writeFit(pdState.pd, Math.abs(euler.y) < 0.22 ? faceWidth : null)
  }

  /**
   * Once the head turns, the far temple is behind the face. The head proxy is
   * too rough to hide it reliably, so it is switched off; the margin between
   * the two thresholds stops it flickering near the front view.
   */
  hideFarTemple() {
    if (!this.frame || !this.frame.temples) return
    const THREE = this.three
    this.anchor.updateMatrixWorld(true)
    const depth = (side) => -this.frameRoot.localToWorld(new THREE.Vector3(side * 70, 10, -8)).z
    const gap = depth(1) - depth(-1)
    if (Math.abs(gap) > FAR_TEMPLE_HIDE_MM) this.farSide = gap > 0 ? 1 : -1
    else if (Math.abs(gap) < FAR_TEMPLE_SHOW_MM) this.farSide = 0
    this.frame.temples.forEach((temple) => {
      temple.visible = temple.userData.side !== this.farSide
    })
  }

  /**
   * Hair drawn over the temples. The front and lenses stay on top.
   */
  drawOcclusion() {
    const context = this.occludeCanvas.getContext('2d')
    context.clearRect(0, 0, STAGE_WIDTH, stageSize.height)
    if (!this.hairCanvas.width || !this.frame) return
    const THREE = this.three
    const scratch = this.hairScratch
    scratch.width = STAGE_WIDTH
    scratch.height = stageSize.height
    const scratchContext = scratch.getContext('2d')
    scratchContext.clearRect(0, 0, STAGE_WIDTH, stageSize.height)
    scratchContext.drawImage(this.frameCanvas, 0, 0)
    scratchContext.globalCompositeOperation = 'destination-in'
    scratchContext.filter = 'blur(1.5px)'
    scratchContext.drawImage(this.hairCanvas, 0, 0, STAGE_WIDTH, stageSize.height)
    scratchContext.filter = 'none'
    scratchContext.globalCompositeOperation = 'destination-out'
    const half = this.data.front.halfWidthMm + 1
    const corners = [[-half, 26], [half, 26], [half, -26], [-half, -26]].map(([x, y]) => {
      const world = this.frameRoot.localToWorld(new THREE.Vector3(x, y, 0))
      const projected = world.project(this.camera)
      return [(projected.x + 1) / 2 * STAGE_WIDTH, (1 - projected.y) / 2 * stageSize.height]
    })
    scratchContext.beginPath()
    corners.forEach(([x, y], index) => {
      if (index === 0) scratchContext.moveTo(x, y)
      else scratchContext.lineTo(x, y)
    })
    scratchContext.closePath()
    scratchContext.fill()
    scratchContext.globalCompositeOperation = 'source-over'
    context.drawImage(scratch, 0, 0)
  }

  /**
   * @param {number} pd
   * @param {number|null} faceWidth
   */
  writeFit(pd, faceWidth) {
    const fit = judgeFit(pd, faceWidth, this.spec)
    const label = fit === 'large' ? this.copy.fitLarge : fit === 'small' ? this.copy.fitSmall : this.copy.fitClose
    if (this.result) this.result.textContent = label
    const numbers = fill(this.copy.fitNumbers, {
      pd: pd.toFixed(1).replace(/\.0$/, ''),
      dbc: this.spec.dbc,
      width: this.spec.frameWidth
    })
    const face = faceWidth ? ` / ${fill(this.copy.faceWidth, { width: Math.round(faceWidth) })}` : ''
    if (this.numbers) this.numbers.textContent = numbers + face
  }

  rerender() {
    if (!this.lastFace) return
    try {
      this.renderFace(this.lastFace.landmarks, this.lastFace.matrix)
    } catch (error) {
      console.error(error)
    }
  }
}

/**
 * @param {HTMLElement} root
 * @returns {TryOnSession}
 */
function sessionFor(root) {
  if (!roots.has(root)) roots.set(root, new TryOnSession(root))
  return roots.get(root)
}

/**
 * @param {Element} node
 * @returns {TryOnSession|null}
 */
function sessionFromDialog(node) {
  const dialog = node.closest('.js-vtoDialog')
  const root = dialog && document.querySelector(`.js-vtoRoot[data-vto-id="${dialog.dataset.vtoId}"]`)
  return root ? sessionFor(root) : null
}

document.addEventListener('click', (event) => {
  const openButton = event.target.closest('.js-vtoOpen')
  if (openButton) {
    const root = openButton.closest('.js-vtoRoot')
    if (!root) return
    const session = sessionFor(root)
    session.openButton = openButton
    if (openButton.dataset.frame) session.useFrame(openButton.dataset.frame)
    if (session.colors[openButton.dataset.color]) session.color = openButton.dataset.color
    session.open()
    return
  }
  const frameButton = event.target.closest('.js-vtoFrameButton')
  if (frameButton) {
    const session = sessionFromDialog(frameButton)
    if (session) {
      session.setFrame(frameButton.dataset.frame).catch((error) => {
        console.error(error)
        session.setStatus(session.copy.error)
      })
    }
    return
  }
  const closeButton = event.target.closest('.js-vtoClose')
  if (closeButton) {
    const session = sessionFromDialog(closeButton)
    if (session) session.close()
    return
  }
  const colorButton = event.target.closest('.js-vtoColor')
  if (colorButton) {
    const session = sessionFromDialog(colorButton)
    if (session && session.renderer) {
      session.setColor(colorButton.dataset.color).catch((error) => {
        console.error(error)
        session.setStatus(session.copy.error)
      })
    }
    return
  }
  const tab = event.target.closest('.js-vtoTab')
  if (tab) {
    const session = sessionFromDialog(tab)
    if (session) session.selectPanel(tab.dataset.panel)
    return
  }
  const photoButton = event.target.closest('.js-vtoPhoto')
  if (photoButton) {
    const dialog = photoButton.closest('.js-vtoDialog')
    const input = dialog && dialog.querySelector('.js-vtoFile')
    if (input) input.click()
  }
})

document.addEventListener('change', (event) => {
  const input = event.target
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('js-vtoFile')) return
  const file = input.files && input.files[0]
  const session = sessionFromDialog(input)
  if (file && session) session.usePhoto(file)
  input.value = ''
})

document.addEventListener('input', (event) => {
  const input = event.target
  if (!(input instanceof HTMLInputElement)) return
  const session = input.closest('.js-vtoDialog') ? sessionFromDialog(input) : null
  if (!session) return
  if (input.classList.contains('js-vtoYaw')) {
    session.debugYaw = Number(input.value) * Math.PI / 180
    session.rerender()
    return
  }
  if (input.classList.contains('js-vtoPd') || input.classList.contains('js-vtoPosition')) {
    if (session.mode === 'photo') session.rerender()
  }
})

const sampleParams = new URLSearchParams(window.location.search)
const sampleUrl = sampleParams.get('vtoSample')
if (sampleUrl) {
  const startSample = () => {
    const root = document.querySelector('.js-vtoRoot')
    if (!root) return
    const session = sessionFor(root)
    const frame = sampleParams.get('vtoFrame')
    if (frame) session.useFrame(frame)
    const color = sampleParams.get('vtoColor')
    if (color && session.colors[color]) session.color = color
    session.dialog.hidden = false
    document.body.appendChild(session.dialog)
    document.body.classList.add('e8-vto-open')
    if (session.fitStage()) session.applyStage()
    window.addEventListener('resize', session.onResize)
    session.syncColorButtons()
    fetch(sampleUrl)
      .then((response) => response.blob())
      .then((blob) => session.usePhoto(new File([blob], 'sample.jpg', { type: blob.type || 'image/jpeg' })))
      .catch((error) => {
        console.error(error)
        session.setStatus(session.copy.error)
      })
  }
  if (document.readyState === 'loading') window.addEventListener('DOMContentLoaded', startSample)
  else startSample()
} else if (sampleParams.has('tryon')) {
  const startTryOn = () => {
    const root = document.querySelector('.js-vtoRoot')
    if (!root) return
    const session = sessionFor(root)
    const color = sampleParams.get('tryon')
    if (color && session.colors[color]) session.color = color
    session.open().catch((error) => {
      console.error(error)
      session.setStatus(session.copy.error)
    })
  }
  if (document.readyState === 'loading') window.addEventListener('DOMContentLoaded', startTryOn)
  else startTryOn()
}
